import uuid from 'node-uuid';
import moment from 'moment';
import { get, head, has, defaultTo } from 'lodash';

import {
	getMyPaymentsSortAscending,
	myPaymentsToAuthorise,
	otherUserPaymentsToAuthorise,
	allAccountPaymentsToAuthorise,
	getMyPaymentsSortColumn,
	getOtherUserPaymentsSortColumn,
} from './selectors';
import {
	getAccountRights,
	getAccountById,
	getAccountByAccountNoAndSortCode,
	getAccountIdByAccountNo,
	getAccountByAccountNumber,
} from '../accounts/selectors';
import { getUserId } from '../user/selectors';

import { formatDate, getDate } from '../../utils/dateUtils';
import { formatAmount, formatIFTAmount } from '../../utils/numberUtils';
import {
	getFormattedFromAndToAccounts,
	formatAccountForTable,
	formatIFTAccountForTable,
	formatAccountForSort,
} from '../../utils/accountUtils';
import {
	sortMyPaymentsDateComparison,
	getPaymentStopOption,
	isFileUploadPayment,
	sortByDate,
	sortByString,
	sortByAmount,
} from '../../utils/paymentsUtils';
import {
	paymentFrequencyLabelMap,
	TYPE_BACS_CREDIT,
	BACS_CREDIT_CODE,
	TYPE_FASTER_PAYMENTS,
	TYPE_BACS_DEBIT,
	TYPE_IFT,
	TYPE_CHAPS,
	TYPE_BACS,
} from '../../config/index';
import {
	CONST_INSTRUCTED,
	CONST_EQUIVALENT,
	CONST_AMOUNT_UPPERCASE,
	UPLOAD,
	PENDING,
	SOFT,
} from '../../constants/Constants';

export const getMyPaymentsToAuthoriseCount = (state) =>
	myPaymentsToAuthorise(state).filter((payment) => payment.status !== 'EXPIRED').length;
export const getOtherUserPaymentsToAuthoriseCount = (state) =>
	otherUserPaymentsToAuthorise(state).length + allAccountPaymentsToAuthorise(state).length;

const isRepeatingPayment = (payment) => {
	const payments = get(payment, 'payments', []);
	const paymentDetails = head(payments);
	if (paymentDetails && paymentDetails.frequency && paymentDetails.frequency !== 'SINGLE ONE OFF') {
		return true;
	}
	return false;
};

const getAccount = (state, { account_uuid = '', account_number: accountNumber, sort_code: sortCode }) => {
	if (account_uuid) return getAccountById(state, account_uuid);
	if (!sortCode) {
		return getAccountByAccountNumber(state, accountNumber);
	}
	return getAccountByAccountNoAndSortCode(state, { accountNumber, sortCode });
};

const getBatchLine = (item, accountRights, paymentAuthType, isBacsDebit) => {
	const to = isBacsDebit
		? ''
		: formatAccountForTable(null, {
				...item.to_account,
				name: item.beneficiary_name || item.to_account.name,
		  });
	const from = isBacsDebit
		? formatAccountForTable(null, {
				...item.from_account,
				name: item.beneficiary_name,
		  })
		: '';

	return {
		id: item.id,
		date: '',
		from,
		to,
		amount: formatAmount(item.amount),
		reference: item.reference,
		status: '',
		accountRights,
		paymentAuthType,
		isBatchLine: true,
	};
};

const getFromAndToAccounts = (state, paymentDetails, isBacsDebit) => {
	if (isBacsDebit) {
		return {
			toAccount: getAccount(state, paymentDetails.to_account),
			fromAccount: {
				...paymentDetails.from_account,
				name: paymentDetails.beneficiary_name,
			},
		};
	}
	return {
		toAccount: {
			...paymentDetails.to_account,
			name: get(paymentDetails, 'beneficiary_name', get(paymentDetails, 'to_account.name')),
		},
		fromAccount: getAccount(state, paymentDetails.from_account, paymentDetails.type),
	};
};
export const formatIFTPaymentForListing = (payment, state) => {
	const { ift, verification_type: paymentAuthType, status } = payment;
	const accountRights = getAccountRights(state, ift.from_account);
	const fromAccountId = getAccountIdByAccountNo(state, get(ift, 'from_account.account_number'));
	const instructedAmount = get(ift, 'payment_amount', []).find((amount) => amount.type === CONST_INSTRUCTED);
	const equivalentAmount = get(ift, 'payment_amount', []).find((amount) => amount.type === CONST_EQUIVALENT);
	const creditorAmount =
		instructedAmount.currency === get(ift, 'debtor_currency', '') ? equivalentAmount : instructedAmount;
	const { fromAccount, toAccount } = getFromAndToAccounts(state, ift);
	return {
		...ift,
		id: payment.verification_id,
		date: getDate(ift.submission_date),
		accountRights,
		paymentAuthType,
		from: formatAccountForTable(undefined, fromAccount),
		to: formatIFTAccountForTable(toAccount.name, toAccount.iban || toAccount.account_number),
		status,
		amount: formatIFTAmount(creditorAmount.value, creditorAmount.currency),
		fromAccountId,
		type: TYPE_IFT,
		amountSort: creditorAmount.value || 0,
	};
};

export const ifFileUpload = (payment) => {
	if (payment.type === TYPE_BACS) return get(payment, 'bacs.file_upload', false);
	if (payment.type === TYPE_CHAPS) return get(head(payment.chaps), 'file_upload', false);
	return get(head(payment.payments), 'file_upload', false);
};

// called by getMyPaymentsList
export const formatPaymentList = (paymentList, state) =>
	paymentList.reduce((result, paymentRow) => {
		if (paymentRow.ift) {
			const iftPayment = formatIFTPaymentForListing(paymentRow, state);
			return [...result, iftPayment];
		}
		const isBatch = paymentRow.payments.length > 1 || !!paymentRow.bacs;
		const paymentDetails = head(paymentRow.payments);
		const { showBatch = false, status, verification_type: paymentAuthType } = paymentRow;
		const isBacsDebit = get(paymentRow, 'bacs.type', '') === 'BDB';
		const isChaps = get(paymentRow, 'type', '') === TYPE_CHAPS;
		const { fromAccount = {}, toAccount = {} } = getFromAndToAccounts(state, paymentDetails, isBacsDebit, isChaps);
		const { fromAccountFormatted, toAccountFormatted, fromSort, toSort } = getFormattedFromAndToAccounts(
			paymentDetails,
			fromAccount,
			toAccount,
			isBacsDebit
		);
		const file_upload = ifFileUpload(paymentRow);

		const amount = formatAmount(isBatch ? paymentRow.total : paymentDetails.amount);
		const amountSort = isBatch ? paymentRow.total : paymentDetails.amount;

		const accountRights = isBacsDebit ? getAccountRights(state, toAccount) : getAccountRights(state, fromAccount);
		const paymentType = isChaps ? TYPE_CHAPS : get(paymentDetails, 'type', get(paymentRow, 'type'));
		result.push({
			id: paymentRow.verification_id,
			date: getDate(paymentDetails.start_date),
			from: fromAccountFormatted,
			to: toAccountFormatted,
			amount,
			reference: isBatch ? ' - ' : paymentDetails.reference,
			status,
			accountRights,
			paymentAuthType,
			frequency: paymentDetails.frequency,
			end_date: paymentDetails.end_date,
			creator_name: paymentDetails.creator_name,
			file_upload,
			isBatch,
			showBatch,
			...(isBatch ? { batch: paymentRow.payments } : {}),
			fromAccount,
			...(isBatch ? {} : { toAccount }),
			type: paymentType,
			fromAccountId: fromAccount.account_uuid,
			amountSort,
			isBacsDebit,
			fromSort,
			toSort,
		});

		return result;
	}, []);

export const formatPostCutoffPayment = (paymentList, state) => {
	const list = formatPaymentList(paymentList, state);
	return list.map((item) => {
		if (item.frequency) {
			const stopsWhen = item.end_date ? formatDate(item.end_date) : 'When it is cancelled';
			const frequency =
				item.frequency in paymentFrequencyLabelMap ? paymentFrequencyLabelMap[item.frequency].label : item.frequency;
			return { ...item, frequency, end_date: stopsWhen };
		}
		return item;
	});
};

const sortByCompareFunc = {
	date: sortByDate(),
	reference: sortByString('reference'),
	amount: sortByAmount(),
	from: sortByString('fromSort'),
	to: sortByString('toSort'),
};

export const expandBatchMyPayments = (paymentList) =>
	paymentList.reduce((result, paymentRow) => {
		result.push(paymentRow);

		if (paymentRow.showBatch) {
			const { accountRights, isBacsDebit, paymentAuthType } = paymentRow;
			const payments = get(paymentRow, 'batch', []);

			payments.forEach((item) => {
				result.push(getBatchLine(item, accountRights, paymentAuthType, isBacsDebit));
			});
		}
		return result;
	}, []);

export const getMyPaymentsToAuthorise = (state) => {
	const { id, sortDirection } = getMyPaymentsSortColumn(state);
	const paymentsToAuthList = myPaymentsToAuthorise(state).filter((payment) => payment.status !== 'CUTOFF');
	const formattedPayments = formatPaymentList(paymentsToAuthList, state);
	const compareFunc = sortByCompareFunc[id](sortDirection);
	const sortedPayments = formattedPayments.sort(compareFunc);
	const expandedPayments = expandBatchMyPayments(sortedPayments);
	return expandedPayments;
};

export const getMyPostCutOffPayments = (state) => {
	const paymentsToAuthList = myPaymentsToAuthorise(state).filter((payment) => payment.status === 'CUTOFF');

	paymentsToAuthList.sort(sortMyPaymentsDateComparison(getMyPaymentsSortAscending(state)));
	return formatPostCutoffPayment(paymentsToAuthList, state);
};

const getBatch = (payment) => ({
	batch: payment.batch.map((payment) => ({
		...payment,
		amount: payment.amount.value,
	})),
});

export const getOthersIFTPayment = (state, payment, userId) => {
	const accountRights = getAccountRights(state, payment.from_account);
	const fromAccountId = getAccountIdByAccountNo(state, get(payment, 'from_account.account_number')) || '';
	const instructedAmount = get(payment, 'payment_amount', []).find((amount) => amount.type === CONST_INSTRUCTED);
	const equivalentAmount = get(payment, 'payment_amount', []).find((amount) => amount.type === CONST_EQUIVALENT);
	const creditorAmount =
		instructedAmount.currency === get(payment, 'debtor_currency', '') ? equivalentAmount : instructedAmount;
	const { type: paymentType } = payment;
	return {
		...payment,
		date: getDate(payment.submission_date),
		accountRights,
		from: formatIFTAccountForTable(payment.from_account.name, payment.from_account.account_number),
		to: formatIFTAccountForTable(payment.to_account.name, payment.to_account.account_number || payment.to_account.iban),
		amount: formatIFTAmount(creditorAmount.value, creditorAmount.currency),
		createdByMe: userId === payment.creator_id,
		fromAccountId,
		fromSort: payment.from_account.name || payment.from_account.account_number || '',
		toSort: payment.to_account.name || payment.to_account.account_number || payment.to_account.iban || '',
		amountSort: creditorAmount.value || 0,
		paymentType,
	};
};
// Called by OtherPayments
const getFasterPayment = (state, payment, userId) => {
	const fromAccount = getAccount(state, payment.from_account);
	const to = formatAccountForTable(payment.batch_name, payment.to_account || {});
	const from = fromAccount ? formatAccountForTable(null, fromAccount) : '';
	const amount = formatAmount(payment.amount.value);
	const accountRights = getAccountRights(state, payment.from_account);
	const date = getDate(payment.start_date);
	const batch = payment.isBatch ? getBatch(payment) : {};
	const toAccount = payment.isBatch ? {} : { toAccount: payment.to_account };
	const { showBatch = false, status, reference, start_date, end_date, frequency, type: paymentType } = payment;
	const isChaps = get(payment, 'type') === TYPE_CHAPS;
	const fromAccountId = isChaps ? getAccountIdByAccountNo(state, get(fromAccount, 'account_number')) : '';
	const type = isChaps ? { type: TYPE_CHAPS } : {};
	const batchUpload = payment.batch_type === UPLOAD;
	const fromSort = formatAccountForSort(null, fromAccount);
	const toSort = formatAccountForSort(get(batch, 'batch.batch_name'), toAccount);
	const amountSort = payment.amount.value || 0;

	const result = [
		{
			id: payment.id,
			date,
			start_date,
			end_date,
			frequency,
			from,
			to,
			amount,
			reference,
			status,
			isBatch: !!payment.isBatch,
			showBatch,
			createdByMe: userId === payment.creator_id,
			accountRights,
			...batch,
			fromAccount: payment.from_account,
			...toAccount,
			fromAccountId,
			...type,
			batchUpload,
			fromSort,
			toSort,
			amountSort,
			paymentType,
		},
	];

	return result;
};

// Called by OtherPayments
const getBacsPayment = (state, payment, userId) => {
	const isBacsDebit = get(payment, 'bacsType', '') === 'BDB';
	const { showBatch = false, status, reference, start_date, end_date, frequency, total, type: paymentType } = payment;

	const account = getAccount(state, payment.account) || {};
	const paymentName = get(payment, 'bacs_name', '');
	const to = isBacsDebit ? formatAccountForTable(null, account) : formatAccountForTable(paymentName);
	const from = isBacsDebit ? formatAccountForTable(paymentName) : formatAccountForTable(null, account);

	const toSort = isBacsDebit ? formatAccountForSort(null, account) : formatAccountForSort(paymentName);
	const fromSort = isBacsDebit ? formatAccountForSort(paymentName) : formatAccountForSort(null, account);
	const batch = payment.entries;

	const amount = formatAmount(total);
	const amountSort = total || 0;
	const accountRights = getAccountRights(state, account);
	const date = getDate(start_date);
	const batchUpload = payment.batch_type === UPLOAD;

	const result = [
		{
			id: payment.id,
			date,
			start_date,
			end_date,
			frequency,
			from,
			to,
			amount,
			reference,
			status,
			isBatch: true,
			showBatch,
			createdByMe: userId === payment.creator_id,
			accountRights,
			batch,
			batchUpload,
			amountSort,
			fromSort,
			toSort,
			paymentType,
			isBacsDebit,
		},
	];

	return result;
};

const sortByOtherUserCompareFunc = {
	date: sortByDate(),
	reference: sortByString('reference'),
	amount: sortByAmount(),
	from: sortByString('fromSort'),
	to: sortByString('toSort'),
};

export const expandBatchOtherPayments = (paymentList) =>
	paymentList.reduce((result, paymentRow) => {
		result.push(paymentRow);

		if (paymentRow.showBatch) {
			const isBacs = paymentRow.paymentType === TYPE_BACS;
			const { isBacsDebit } = paymentRow;

			const payments = get(paymentRow, 'batch', []);

			payments.forEach((item) => {
				/* eslint-disable one-var */
				let to,
					from = '';
				const amount = get(item, 'amount.value', get(item, 'amount'));
				const formattedAmount = formatAmount(amount);

				if (isBacs) {
					// BACS Batch
					to = isBacsDebit ? '' : formatAccountForTable(null, item.account);
					from = isBacsDebit ? formatAccountForTable(null, item.account) : '';
				} else {
					// FPS & CHAPS Batch
					to = formatAccountForTable(null, item.to_account);
				}

				result.push({
					id: item.id,
					date: '',
					from,
					to,
					amount: formattedAmount,
					reference: item.reference,
					status: '',
					isBatchLine: true,
				});
			});
		}
		return result;
	}, []);

export const getOtherUserPaymentsToAuthorise = (state) => {
	const userId = getUserId(state);
	const paymentsToAuthList = [...otherUserPaymentsToAuthorise(state), ...allAccountPaymentsToAuthorise(state)];

	const formattedList = paymentsToAuthList.reduce((result, payment) => {
		const isBacs = payment.type === TYPE_BACS;
		const isIFT = payment.type === TYPE_IFT;

		if (isBacs) {
			return [...result, ...getBacsPayment(state, payment, userId)];
		}
		if (isIFT) {
			return [...result, getOthersIFTPayment(state, payment, userId)];
		}
		return [...result, ...getFasterPayment(state, payment, userId)];
	}, []);
	const { id, sortDirection } = getOtherUserPaymentsSortColumn(state);
	const compareFunc = sortByOtherUserCompareFunc[id](sortDirection);
	const sortedPayments = formattedList.sort(compareFunc);

	const expandedPayments = expandBatchOtherPayments(sortedPayments);
	return expandedPayments;
};

export const getActiveAwaitingAuthTab = (state) => state.payments.activeAwaitingAuthTab;

export const paymentWithAccount = (state, payment) => ({
	...payment,
	from_account: getAccount(state, payment.from_account) || payment.from_account,
	to_account: getAccount(state, payment.to_account) || payment.to_account,
});

export const getToViewForOthers = (state) => {
	const otherPaymentsToAuthList = allAccountPaymentsToAuthorise(state);
	const payment = otherPaymentsToAuthList.find((payment) => payment.id === state.payments.toAuthorise);
	return payment;
};

export const getToAuthorise = (state) => {
	const paymentsToAuthList = myPaymentsToAuthorise(state);
	const payment = paymentsToAuthList.find((payment) => payment.verification_id === state.payments.toAuthorise);
	return {
		...payment,
		payments: get(payment, 'payments', []).map((payment) => paymentWithAccount(state, payment)),
	};
};

const getToViewMySinglePayment = (myPaymentsToAuth, toView) => {
	const payment = myPaymentsToAuth.find((payment) => payment.verification_id === toView);
	return payment;
};

const getToViewIndividualBatchPayment = (myPaymentsToAuth, toView) => {
	const batch = myPaymentsToAuth.find((payment) => payment.payments.some((p) => p.id === toView));
	if (!batch) return null;
	const payments = batch.payments.filter((p) => p.id === toView);
	const result = {
		...batch,
		payments,
	};
	return result;
};

export const buildToViewIFTForOtherPayment = (payment) => ({
	isThirdParty: true,
	created_timestamp: payment.created_timestamp,
	verification_id: payment.id,
	type: payment.type,
	status: payment.status,
	approvers: payment.approvers,
	payments: [payment],
});

const buildToViewModelForOtherPayment = (payment, state) => {
	if (payment.type === TYPE_IFT) {
		return buildToViewIFTForOtherPayment(payment);
	}
	const to_account = payment.bacsType === 'BDB' ? getAccount(state, payment.to_account) : payment.to_account;
	const from_account = payment.bacsType === 'BDB' ? payment.from_account : getAccount(state, payment.from_account);
	const amount = payment.isBacs ? payment.amount : payment.amount.value;

	return {
		isThirdParty: true,
		status: payment.status,
		created_timestamp: payment.created_timestamp,
		total: amount,
		approvers: payment.approvers,
		verification_id: payment.id,
		bacsType: payment.bacsType,
		type: payment.type,
		payments: [
			{
				...payment,
				amount,
				from_account,
				to_account,
				creator_name: payment.created_by,
			},
		],
	};
};

const getToViewOtherSinglePayment = (othersPaymentsToAuth, toView, state) => {
	const payment = othersPaymentsToAuth.find((payment) => payment.id === toView);
	const result = payment ? buildToViewModelForOtherPayment(payment, state) : null;
	return result;
};

const mapEntryToPayment = (entry, bacsDetails) => {
	const {
		created_by,
		bacs_name: batch_name,
		submission_date: start_date,
		created_timestamp,
		approvers,
		id,
		bacsType,
		submission_date,
		settlement_date,
		bacs_id,
	} = bacsDetails;

	const { reference, amount, status, id: entryId, hmrc_reference, transaction_code } = entry;
	const from_account = bacsDetails.bacsType === BACS_CREDIT_CODE ? bacsDetails.account : entry.account;
	const to_account = bacsDetails.bacsType === BACS_CREDIT_CODE ? entry.account : bacsDetails.account;
	const beneficiary_name = to_account.name;

	return {
		isBacs: true,
		amount: get(amount, 'value'),
		from_account,
		to_account,
		reference,
		start_date,
		batch_name,
		created_by,
		id,
		beneficiary_name,
		status,
		created_timestamp,
		approvers,
		entryId,
		bacsType,
		hmrc_reference,
		submission_date,
		settlement_date,
		bacs_id,
		transaction_code,
	};
};

const getToViewOtherIndividualBatchPayment = (othersPaymentsToAuth, toView, state) => {
	const batchPayments = othersPaymentsToAuth.reduce((payments, payment) => {
		if (payment.isBatch) {
			return [...payments, ...payment.batch];
		}
		if (payment.type === TYPE_BACS) {
			const { entries, ...bacsDetails } = payment;
			const entriesWithDetails = entries.map((entry) => mapEntryToPayment(entry, bacsDetails));
			return [...payments, ...entriesWithDetails];
		}
		return payments;
	}, []);

	const payment = batchPayments.find((payment) =>
		payment.isBacs ? payment.entryId === toView : payment.id === toView
	);
	return payment ? buildToViewModelForOtherPayment(payment, state) : null;
};

export const paymentToViewWithAccount = (state, payment) => {
	const { payments } = payment;
	return {
		...payment,
		payments: [
			{
				...paymentWithAccount(state, head(payments)),
			},
		],
	};
};
export const getToView = (state) => {
	const myPaymentsToAuth = myPaymentsToAuthorise(state);
	let payment = getToViewMySinglePayment(myPaymentsToAuth, state.payments.toView);
	if (payment) return paymentToViewWithAccount(state, payment);

	payment = getToViewIndividualBatchPayment(myPaymentsToAuth, state.payments.toView);
	if (payment) return paymentToViewWithAccount(state, payment);

	const othersPaymentsToAuth = [...otherUserPaymentsToAuthorise(state), ...allAccountPaymentsToAuthorise(state)];

	payment = getToViewOtherSinglePayment(othersPaymentsToAuth, state.payments.toView, state);
	if (payment) {
		return payment;
	}

	return getToViewOtherIndividualBatchPayment(othersPaymentsToAuth, state.payments.toView, state);
};

export const isBatch = (state) => {
	const payment = getToAuthorise(state);
	if (payment.type === TYPE_BACS) {
		return false;
	}
	return get(payment, 'payments', []).length > 1 || get(head(payment.payments), 'file_upload', false);
};

export const isToAuthoriseBacs = (state) => {
	const payment = getToAuthorise(state);
	return payment.type === TYPE_BACS;
};

export const isChaps = (state) => {
	const payment = getToAuthorise(state);
	return payment.type === TYPE_CHAPS;
};

export const getBacsTypePaymentToAuthorise = (state) => getToAuthorise(state).bacsType;

export const isRepeatingPaymentToAuthorise = (state) => {
	const toAuthorise = getToAuthorise(state);
	return isRepeatingPayment(toAuthorise);
};

export const isRepeatingPaymentToView = (state) => {
	const toView = getToView(state);
	return isRepeatingPayment(toView);
};

export const getBatchName = (state) => {
	if (isBatch(state)) {
		const toAuthorise = getToAuthorise(state);
		const payments = get(toAuthorise, 'payments', []);
		const payment = head(payments);
		return payment.batch_name;
	}
	return null;
};

export const getBacsName = (state) => {
	if (isToAuthoriseBacs(state)) {
		const toAuthorise = getToAuthorise(state);
		const payments = get(toAuthorise, 'payments', []);
		const payment = head(payments);
		return payment.batch_name;
	}
	return null;
};

export const getPaymentTypeForMyPayment = (payment) => {
	let paymentType = TYPE_FASTER_PAYMENTS;
	if (has(payment, TYPE_BACS)) {
		paymentType = get(payment, 'bacs.type') === BACS_CREDIT_CODE ? TYPE_BACS_CREDIT : TYPE_BACS_DEBIT;
	}
	if (has(payment, 'chaps')) {
		paymentType = TYPE_CHAPS;
	}
	return paymentType;
};
export const getPaymentTypeForOthersPayment = (payment) => {
	let paymentType = TYPE_FASTER_PAYMENTS;
	if (has(payment, 'bacs_id')) {
		paymentType = get(payment, 'bacsType') === BACS_CREDIT_CODE ? TYPE_BACS_CREDIT : TYPE_BACS_DEBIT;
	}
	if (get(payment, 'type', '') === TYPE_CHAPS) {
		paymentType = TYPE_CHAPS;
	}
	return paymentType;
};

export const getBacsPaymentAmount = (amount) => get(amount, 'value', amount).toString();
const formatBacsPayment = (bacs, state) => {
	const isBacsCredit = get(bacs, 'bacsType', get(bacs, 'type')) === BACS_CREDIT_CODE;
	const bacsDetails = {
		id: bacs.id,
		bacsId: bacs.bacs_id,
		paymentName: bacs.bacs_name,
		isInternalPayment: get(bacs, 'is_internal_payment'),
		submissionDate: bacs.submission_date ? moment(bacs.submission_date) : undefined,
		settlementDate: bacs.settlement_date ? moment(bacs.settlement_date) : undefined,
		...(isBacsCredit && {
			fromAccount: getAccount(state, bacs.account).account_uuid,
		}),
		...(!isBacsCredit && {
			toAccount: getAccount(state, bacs.account).account_uuid,
		}),
	};

	const payments = get(bacs, 'entries', []).map(
		({
			amount,
			reference,
			hmrc_reference: hmrcReference,
			transaction_code: transactionCode,
			account,
			is_internal_payment,
		}) => ({
			id: uuid.v1(),
			amount: getBacsPaymentAmount(amount),
			reference,
			isInternalPayment: is_internal_payment,
			...(isBacsCredit && { hmrcReference, toAccount: account }),
			...(!isBacsCredit && { transactionCode, fromAccount: account }),
		})
	);
	return {
		paymentDetails: { bacsDetails },
		payments,
	};
};
const transformBatchPaymentToArray = (batch) =>
	batch.map(({ amount, reference, to_account, from_account, start_date, is_internal_payment }) => ({
		id: uuid.v1(),
		toAccount: to_account,
		fromAccount: from_account,
		amount: amount.toString(),
		reference,
		date: start_date,
		isInternalPayment: is_internal_payment,
	}));

const formatFasterPayments = (payment) => {
	const batch = get(payment, 'payments', []);
	const isBatch = batch.length > 1;

	/* eslint-disable indent */
	const paymentList = isBatch
		? transformBatchPaymentToArray(batch)
		: [
				{
					id: payment.verification_id,
					toAccount: {
						name: get(payment, 'beneficiary_name'),
						...payment.to_account,
					},
					fromAccount: payment.from_account,
					amount: payment.amount.toString(),
					reference: payment.reference,
					date: payment.start_date,
					frequency: payment.frequency,
					stopPaymentOption: getPaymentStopOption(payment),
					stopPaymentOnDate: payment.end_date ? moment(payment.end_date) : payment.end_date,
					isInternalPayment: get(payment, 'is_internal_payment'),
				},
		  ];
	const batchDetails = isBatch
		? {
				isBatch: true,
				name: get(head(batch), 'batch_name', ''),
				id: payment.verification_id,
		  }
		: undefined;
	return {
		payments: paymentList,
		paymentDetails: { batchDetails },
	};
};
export const formatMyPaymentsToEdit = (state, payment) => {
	const paymentType = getPaymentTypeForMyPayment(payment);
	switch (paymentType) {
		case TYPE_BACS_CREDIT:
		case TYPE_BACS_DEBIT:
			return { paymentType, ...formatBacsPayment(payment.bacs, state) };
		case TYPE_CHAPS:
		case TYPE_FASTER_PAYMENTS:
			return {
				paymentType,
				...formatFasterPayments({ ...payment, ...payment.payments[0] }),
			};
		default:
			return payment;
	}
};

export const formatOtherPaymentsToEdit = (state, payment) => {
	const paymentType = getPaymentTypeForOthersPayment(payment);
	let paymentToFormat;
	switch (paymentType) {
		case TYPE_BACS_CREDIT:
		case TYPE_BACS_DEBIT:
			return { paymentType, ...formatBacsPayment(payment, state) };
		case TYPE_CHAPS:
		case TYPE_FASTER_PAYMENTS:
			paymentToFormat = {
				...payment,
				amount: payment.amount.value,
				verification_id: payment.id,
				payments: payment.isBatch
					? payment.batch.map((payment) => ({
							...payment,
							amount: get(payment, 'amount.value', ''),
					  }))
					: [],
			};

			return { paymentType, ...formatFasterPayments(paymentToFormat) };
		default:
			return payment;
	}
};

export const getPaymentToEdit = (state) => {
	const myPaymentsToAuth = myPaymentsToAuthorise(state);
	let payment = myPaymentsToAuth.find((payment) => payment.verification_id === state.payments.toEdit);
	if (payment) {
		return formatMyPaymentsToEdit(state, payment);
	}
	const othersPaymentsToAuth = [...otherUserPaymentsToAuthorise(state), ...allAccountPaymentsToAuthorise(state)];
	payment = othersPaymentsToAuth.find((payment) => payment.id === state.payments.toEdit);
	if (payment) {
		return formatOtherPaymentsToEdit(state, payment);
	}

	return payment;
};

export const getPaymentIdToAuthorise = (state) => state.payments.toAuthorise;

export const getPaymentCharge = (payments) => {
	const payment = head(payments);
	const chargeAmounts = get(payment, 'charge_amount', []);
	const fxCharge = chargeAmounts.find((chargeAmount) => chargeAmount.type === CONST_AMOUNT_UPPERCASE);
	return { [payment.id]: defaultTo(fxCharge, {}) };
};

export const isFileUploadDeadlineMessage = (state) => {
	const toAuthorise = getToAuthorise(state);
	const { verification_type, status } = toAuthorise;
	const isFileUploadBatchPayment = isFileUploadPayment(toAuthorise);
	const pendingCheck = verification_type === SOFT && status === PENDING;
	return (isChaps(state) || isToAuthoriseBacs(state)) && isFileUploadBatchPayment && pendingCheck;
};
